/**
 * In-flow connector form for onboarding (GA4 / BigQuery / Firebase / Mixpanel).
 * Reuses connector guides + schemas; handshake from real create + test results.
 */

import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { navigate } from '../../core/router.js';
import { toast } from '../../components/toast.js';
import { renderFields, readFields } from '../connectors/fields.js';
import { SCHEMAS, collectCredential, gcpAutofill } from '../connectors/schemas.js';
import {
  PROVIDER_GUIDES, CRED_UNLOCKS, credFindCopy,
} from '../connectors/guides.js';
import { linkifyGuideStep } from '../connectors/guide-panel.js';
import { IN_FLOW_SOURCES } from './model.js';
import { credentialParsed, handshakeFromResult } from './handshake.js';
import { discardPendingConnect, saveConnectAttempt } from './save-connect.js';

const REGIONS = [
  { value: 'us', label: 'United States' },
  { value: 'eu', label: 'EU' },
  { value: 'in', label: 'India' },
];

/**
 * @param {object} state
 * @param {{
 *   me: object,
 *   productName: string,
 *   item: object,
 *   source: string,
 *   onBack: () => void,
 *   onConnected: () => void,
 * }} opts
 */
export function renderConnectStep(state, opts) {
  const source = opts.source;
  const meta = IN_FLOW_SOURCES[source] || IN_FLOW_SOURCES.ga4;
  const slug = meta.provider;
  const schema = SCHEMAS[slug];
  const item = opts.item;
  const root = el('div', { class: 'np-connect' });
  const local = {
    dead: false,
    saving: false,
    region: 'us',
    values: {},
    hs: { parsed: 'wait', reachable: 'wait', sample: 'wait' },
    error: '',
    success: false,
    /** Set after first successful create; retries PUT this id instead of POSTing again. */
    pendingId: null,
    depthAfter: (CRED_UNLOCKS[slug] && CRED_UNLOCKS[slug].depth) || 35,
    guideOpen: false,
  };

  root.addEventListener('bonnie:unmount', () => {
    local.dead = true;
    void abandonPending();
  });
  paint();
  return root;

  function paint() {
    clear(root);
    root.append(
      el('button', {
        type: 'button',
        class: 'np-back',
        onClick: () => void leave(),
      }, '← Back to first connector'),
      el('div', { class: 'np-connect-grid' },
        formColumn(),
        sideColumn()),
    );
  }

  /** Drop a half-saved connector when the user cancels or leaves after a failed test. */
  async function abandonPending() {
    if (local.success || !local.pendingId) return;
    const id = local.pendingId;
    local.pendingId = null;
    await discardPendingConnect(api, id);
  }

  async function leave() {
    await abandonPending();
    if (local.dead) return;
    opts.onBack();
  }

  function formColumn() {
    const guide = PROVIDER_GUIDES[slug] || null;
    const fieldsHost = el('div', { class: 'np-connect-fields' });
    // Autofill + schema fields (same source of truth as Connectors dialog).
    fieldsHost.append(renderFields(schema, slug, { values: local.values, hints: {} }));
    fieldsHost.addEventListener('input', () => {
      local.values = readFields(fieldsHost);
      refreshHandshake(fieldsHost);
    });
    fieldsHost.addEventListener('change', () => {
      local.values = readFields(fieldsHost);
      refreshHandshake(fieldsHost);
    });
    wireRegionChips(fieldsHost);

    const error = el('p', {
      class: 'np-error',
      role: 'alert',
      hidden: local.error ? null : true,
    }, local.error || '');

    const actions = local.success
      ? el('div', { class: 'np-actions' },
        el('button', {
          type: 'button',
          class: 'np-cta',
          onClick: () => navigate('/app/dashboard'),
        }, 'Open dashboard ', el('span', { class: 'arw', 'aria-hidden': 'true' }, '→')))
      : el('div', { class: 'np-actions' },
        el('button', {
          type: 'button',
          class: 'np-cta',
          disabled: local.saving ? true : null,
          onClick: () => submit(fieldsHost, error),
        }, local.saving ? 'Verifying…' : 'Test & save connection'),
        el('button', {
          type: 'button',
          class: 'np-btn',
          onClick: () => void leave(),
        }, 'Cancel'));

    return el('div', { class: 'np-connect-main' },
      el('header', { class: 'np-connect-ident rv' },
        el('span', { class: 'np-connect-mark', 'aria-hidden': 'true' }, (meta.title || '?').charAt(0)),
        el('div', {},
          el('h2', { class: 'np-h' }, `Connect ${meta.title}`),
          el('p', { class: 'np-sub' },
            `Native connector · scoped to ${opts.productName || state.name || 'this product'}`))),
      guideBlock(guide, meta.title),
      error,
      fieldsHost,
      actions);
  }

  function guideBlock(guide, providerName) {
    if (!guide) {
      return el('div', { class: 'np-guide rv' },
        el('p', { class: 'np-lbl' }, 'Where to find these'),
        el('p', { class: 'np-sub' }, credFindCopy(null, providerName)));
    }
    const open = !!local.guideOpen;
    const toggle = el('button', {
      type: 'button',
      class: 'np-guide-toggle',
      'aria-expanded': open ? 'true' : 'false',
      onClick() {
        local.guideOpen = !local.guideOpen;
        paint();
      },
    }, open ? 'Hide step-by-step guide' : 'Open step-by-step guide');
    // Clip button omitted until a real clip exists (task).
    const panel = open
      ? el('div', { class: 'np-guide-panel' },
        el('h3', { class: 'np-guide-title' }, guide.title),
        el('ol', { class: 'np-guide-steps' },
          guide.steps.map((step, index) => el('li', {},
            el('span', { class: 'np-guide-num' }, String(index + 1)),
            el('span', {}, ...linkifyGuideStep(step))))))
      : null;
    return el('div', { class: 'np-guide rv' },
      el('p', { class: 'np-lbl' }, 'Where to find these'),
      el('p', { class: 'np-sub' }, credFindCopy(guide, providerName)),
      el('div', { class: 'np-guide-actions' }, toggle),
      panel);
  }

  function wireRegionChips(host) {
    if (slug !== 'mixpanel') return;
    const select = host.querySelector('[data-cred="region"]');
    if (!select) return;
    const wrap = el('fieldset', { class: 'np-field np-regions' },
      el('legend', { class: 'np-lbl' }, 'Region'),
      el('div', { class: 'np-stages', role: 'group', 'aria-label': 'Region' },
        REGIONS.map((item) => el('button', {
          type: 'button',
          class: item.value === (select.value || 'us') ? 'np-chip is-on' : 'np-chip',
          'aria-pressed': item.value === (select.value || 'us') ? 'true' : 'false',
          onClick() {
            select.value = item.value;
            local.region = item.value;
            select.dispatchEvent(new Event('input', { bubbles: true }));
            wrap.querySelectorAll('.np-chip').forEach((btn) => {
              const on = btn.textContent === item.label;
              btn.classList.toggle('is-on', on);
              btn.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
            refreshHandshake(host);
          },
        }, item.label))));
    select.closest('.cx-field')?.replaceWith(wrap);
    if (!select.value) select.value = 'us';
    // Keep the select in the DOM for readFields (hidden).
    select.hidden = true;
    wrap.append(select);
  }

  function sideColumn() {
    const unlock = CRED_UNLOCKS[slug] || { depth: 35, lines: ['Live data from this provider in chat and Insights'] };
    return el('aside', { class: 'np-connect-side' },
      handshakePanel(),
      el('section', { class: 'np-glass np-unlock rv np-rv-1' },
        el('p', { class: 'np-lbl' }, 'What this unlocks'),
        el('ul', { class: 'np-unlock-list' },
          (unlock.lines || []).map((line) => el('li', {}, line))),
        el('p', { class: 'np-depth-after' },
          `Depth after connecting: ${local.success ? local.depthAfter : unlock.depth}%`)));
  }

  function handshakePanel() {
    const hs = local.hs;
    return el('section', { class: 'np-glass np-handshake rv' },
      el('p', { class: 'np-lbl' }, 'Live handshake'),
      el('ul', { class: 'np-hs-list' },
        hsRow(hs.parsed, 'Credential parsed'),
        hsRow(hs.reachable, reachLabel(hs)),
        hsRow(hs.sample, sampleLabel(hs))),
      el('p', { class: 'np-hs-note' },
        'The same check re-runs when you open Connectors, before every query, and hourly in the background. A badge here always means a check that just passed.'));
  }

  function hsRow(state, label) {
    const cls = state === 'ok' ? 'is-ok' : state === 'fail' ? 'is-fail' : 'is-wait';
    const mark = state === 'ok' ? '✓' : state === 'fail' ? '!' : '';
    return el('li', { class: `np-hs ${cls}` },
      el('span', { class: 'np-hs-dot', 'aria-hidden': 'true' }, mark),
      el('span', {}, label));
  }

  function reachLabel(hs) {
    if (hs.reachable === 'ok' && hs.latency_ms != null) {
      return `Property reachable · ${hs.latency_ms} ms`;
    }
    return 'Property reachable';
  }

  function sampleLabel(hs) {
    if (hs.sample === 'ok' && hs.tool_count != null) {
      const n = hs.tool_count;
      return `Sample query returned ${n} tool${n === 1 ? '' : 's'}`;
    }
    return hs.sample === 'ok' ? 'Sample query ready' : 'Sample query';
  }

  function refreshHandshake(host) {
    const values = readFields(host);
    const parsed = credentialParsed(slug, values, schema);
    local.hs = handshakeFromResult({ parsed });
    const panel = root.querySelector('.np-handshake');
    if (panel) panel.replaceWith(handshakePanel());
  }

  async function submit(fieldsHost, errorNode) {
    if (local.saving || !item || !item.id) {
      toast('Save the product first', 'error');
      return;
    }
    if (!(opts.me && opts.me.role === 'admin')) {
      toast('Ask an admin to connect this source', 'info');
      return;
    }
    const values = readFields(fieldsHost);
    local.values = values;
    const parsed = credentialParsed(slug, values, schema);
    local.hs = handshakeFromResult({ parsed });
    if (!parsed) {
      local.error = 'Fill in the required fields first';
      paint();
      return;
    }
    const built = collectCredential(slug, values, false);
    if (built.error) {
      local.error = built.error;
      local.hs = handshakeFromResult({ parsed: true, createError: built.error });
      paint();
      return;
    }

    local.saving = true;
    local.error = '';
    paint();

    const result = await saveConnectAttempt(api, {
      productId: state.productId,
      providerId: item.id,
      label: values.label || '',
      value: built.value,
      integrationId: local.pendingId,
    });

    if (local.dead) return;

    if (result.integrationId) local.pendingId = result.integrationId;
    local.saving = false;

    if (result.createError || !result.test) {
      local.error = result.error || result.createError || 'Could not save this connector';
      local.hs = handshakeFromResult({
        parsed: true,
        createStatus: result.createStatus,
        createError: local.error,
      });
      paint();
      return;
    }

    if (!result.success || result.test.error) {
      local.error = result.error
        || (result.test && result.test.message)
        || 'Connection saved but the handshake failed';
      local.hs = handshakeFromResult({
        parsed: true,
        test: result.test || { error: local.error },
      });
      // Keep form filled for retry — do not clear fields; next save PUTs pendingId.
      paint();
      return;
    }

    local.hs = handshakeFromResult({ parsed: true, test: result.test });
    if (local.hs.sample !== 'ok' || local.hs.reachable !== 'ok') {
      local.error = local.hs.error
        || (result.test && result.test.message)
        || 'Handshake did not fully succeed';
      paint();
      return;
    }

    local.success = true;
    local.error = '';
    // Depth after an analytics connector matches DEPTH_WEIGHTS.analytics.
    local.depthAfter = (CRED_UNLOCKS[slug] && CRED_UNLOCKS[slug].depth) || 35;
    toast('Connection live', 'success');
    paint();
    if (opts.onConnected) opts.onConnected();
  }
}

/** Re-export for tests that parse a made-up service-account JSON key. */
export { gcpAutofill };
